interface BitmapData {
	width: number;
	height: number;
	data: Uint8ClampedArray;
	imageData: ImageData;
}

/**
 * Load an image from a path and extract its bitmap data
 */
async function loadImageBitmap(imagePath: string, rotation: number = 0): Promise<BitmapData> {
	return new Promise((resolve, reject) => {
		const img = new Image();

		img.onload = () => {
			// Create canvas to extract pixel data
			const canvas = document.createElement('canvas');

			// Check if image is in landscape orientation

			// Calculate total rotation: convert to portrait first if needed
			let totalRotation = rotation;

			// Normalize rotation to 0-359
			totalRotation = ((totalRotation % 360) + 360) % 360;

			// For 90° and 270° rotations, swap width and height
			const isOrthogonal = totalRotation === 90 || totalRotation === 270;
			console.log('Is orthogonal rotation:', isOrthogonal);
			if (isOrthogonal) {
				canvas.width = img.height;
				canvas.height = img.width;
			} else {
				canvas.width = img.width;
				canvas.height = img.height;
			}

			const ctx = canvas.getContext('2d');
			if (!ctx) {
				reject(new Error('Failed to get canvas context'));
				return;
			}

			// Apply rotation if specified
			if (totalRotation !== 0) {
				// Move to center of the canvas (not the image)
				ctx.translate(canvas.width / 2, canvas.height / 2);
				ctx.rotate((totalRotation * Math.PI) / 180);
				// Move back by half of the original image dimensions
				ctx.translate(-img.width / 2, -img.height / 2);
			}

			ctx.drawImage(img, 0, 0);

			console.log('Canvas resolution:', canvas.width, 'x', canvas.height);
			console.log('Original image resolution:', img.width, 'x', img.height);
			console.log('Total rotation applied:', totalRotation);

			// Get the complete bitmap data - use canvas dimensions, not img dimensions
			const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

			resolve({
				width: canvas.width,
				height: canvas.height,
				data: imageData.data,
				imageData: imageData,
			});
		};

		img.onerror = () => {
			reject(new Error(`Failed to load image: ${imagePath}`));
		};

		img.src = imagePath;
	});
}

/**
 * Bicubic interpolation kernel - optimized version
 */
function bicubicKernel(x: number): number {
	const absX = Math.abs(x);

	if (absX <= 1) {
		const x2 = absX * absX;
		const x3 = x2 * absX;
		return 1.5 * x3 - 2.5 * x2 + 1;
	} else if (absX < 2) {
		const x2 = absX * absX;
		const x3 = x2 * absX;
		return -0.5 * x3 + 2.5 * x2 - 4 * absX + 2;
	}
	return 0;
}

/**
 * Get grayscale pixel value with bounds checking (clamp to edge)
 * Converts RGB to grayscale on the fly using Rec.601
 */
function getGrayscalePixelClamped(data: Uint8ClampedArray, width: number, height: number, x: number, y: number): number {
	x = Math.max(0, Math.min(width - 1, x));
	y = Math.max(0, Math.min(height - 1, y));
	const offset = (y * width + x) * 4;
	const r = data[offset];
	const g = data[offset + 1];
	const b = data[offset + 2];
	// Rec.601: Y = 0.299*R + 0.587*G + 0.114*B
	return 0.299 * r + 0.587 * g + 0.114 * b;
}

/**
 * Bicubic interpolation for grayscale at a given position
 * Optimized to work directly with grayscale values
 */
function bicubicInterpolateGrayscale(data: Uint8ClampedArray, width: number, height: number, x: number, y: number): number {
	const x0 = Math.floor(x);
	const y0 = Math.floor(y);
	const dx = x - x0;
	const dy = y - y0;

	// Pre-compute kernel weights for x direction
	const kernelX = [bicubicKernel(dx + 1), bicubicKernel(dx), bicubicKernel(dx - 1), bicubicKernel(dx - 2)];

	// Pre-compute kernel weights for y direction
	const kernelY = [bicubicKernel(dy + 1), bicubicKernel(dy), bicubicKernel(dy - 1), bicubicKernel(dy - 2)];

	let value = 0;

	for (let j = 0; j < 4; j++) {
		for (let i = 0; i < 4; i++) {
			const pixel = getGrayscalePixelClamped(data, width, height, x0 + i - 1, y0 + j - 1);
			value += pixel * kernelX[i] * kernelY[j];
		}
	}

	return value;
}

/**
 * Scale image using bicubic interpolation and convert to grayscale
 * Returns Float32Array of grayscale values
 * Optimized to work directly with grayscale values instead of RGB channels
 */
function scaleImageBicubicToGrayscale(imageData: BitmapData, newWidth: number, newHeight: number): Float32Array {
	const { width, height, data } = imageData;
	const pixelCount = newWidth * newHeight;
	const grayscale = new Float32Array(pixelCount);

	const scaleX = width / newWidth;
	const scaleY = height / newHeight;

	let index = 0;
	for (let y = 0; y < newHeight; y++) {
		for (let x = 0; x < newWidth; x++) {
			// Map to source coordinates (center of the pixel)
			const srcX = (x + 0.5) * scaleX - 0.5;
			const srcY = (y + 0.5) * scaleY - 0.5;

			// Directly interpolate grayscale values (1 interpolation instead of 3)
			grayscale[index++] = bicubicInterpolateGrayscale(data, width, height, srcX, srcY);
		}
	}

	return grayscale;
}

/**
 * Convert RGB to grayscale using Rec.601 luma coefficients
 * Y = 0.299*R + 0.587*G + 0.114*B
 */
export function toGrayscaleRec601(r: number, g: number, b: number): number {
	return 0.299 * r + 0.587 * g + 0.114 * b;
}

/**
 * Compute aHash (Average Hash) of an image
 */
function computeAHash(imageData: BitmapData, hashWidth: number = 16, hashHeight: number = 16): string {
	// Step 1: Scale the image down using bicubic interpolation and convert to grayscale
	const grayscaleValues = scaleImageBicubicToGrayscale(imageData, hashWidth, hashHeight);

	// Step 2: Compute average of all grayscale values
	let sum = 0;
	for (let i = 0; i < grayscaleValues.length; i++) {
		sum += grayscaleValues[i];
	}
	const average = sum / grayscaleValues.length;

	// Step 3: Generate hex directly without intermediate binary string
	// For 16x16 = 256 pixels, we need exactly 64 hex characters (256 bits / 4 bits per hex = 64)
	const pixelCount = grayscaleValues.length;
	const expectedHexLength = Math.ceil(pixelCount / 4);
	const hexChars: string[] = new Array(expectedHexLength);

	/* eslint-disable no-bitwise */
	for (let i = 0; i < expectedHexLength; i++) {
		const baseIdx = i * 4;
		let nibble = 0;
		if (baseIdx < pixelCount && grayscaleValues[baseIdx] >= average) {
			nibble |= 8;
		}
		if (baseIdx + 1 < pixelCount && grayscaleValues[baseIdx + 1] >= average) {
			nibble |= 4;
		}
		if (baseIdx + 2 < pixelCount && grayscaleValues[baseIdx + 2] >= average) {
			nibble |= 2;
		}
		if (baseIdx + 3 < pixelCount && grayscaleValues[baseIdx + 3] >= average) {
			nibble |= 1;
		}
		hexChars[i] = nibble.toString(16);
	}
	/* eslint-enable no-bitwise */

	return hexChars.join('');
}

/**
 * Compute aHash from an image path or data URI (same signature as the original computeAHash)
 * This is the main function that accepts a data URI string or image path
 */
export async function computeAHashFromPath(
	screenshotDataUri: string,
	width: number = 16,
	height: number = 16,
	rotation: number = 0,
): Promise<string> {
	const bitmapData = await loadImageBitmap(screenshotDataUri, rotation);
	return computeAHash(bitmapData, width, height);
}

/**
 * Calculate aHash from an Image object
 *
 * @param {HTMLImageElement} image - The image element to hash
 * @param {number} size - Hash size (default: 16)
 * @param {number} rotation - Rotation angle in degrees (0, 90, 180, 270)
 * @returns {Promise<string>} Hexadecimal hash string
 */
export async function computeAHashFromImageElement(image: HTMLImageElement, size: number = 16, rotation: number = 0): Promise<string> {
	// Create canvas to extract pixel data
	const canvas = document.createElement('canvas');

	// Normalize rotation to 0-359
	const totalRotation = ((rotation % 360) + 360) % 360;

	// For 90° and 270° rotations, swap width and height
	const isOrthogonal = totalRotation === 90 || totalRotation === 270;
	if (isOrthogonal) {
		canvas.width = image.height;
		canvas.height = image.width;
	} else {
		canvas.width = image.width;
		canvas.height = image.height;
	}

	const ctx = canvas.getContext('2d');
	if (!ctx) {
		throw new Error('Failed to get canvas context');
	}

	// Apply rotation if specified
	if (totalRotation !== 0) {
		// Move to center of the canvas (not the image)
		ctx.translate(canvas.width / 2, canvas.height / 2);
		ctx.rotate((totalRotation * Math.PI) / 180);
		// Move back by half of the original image dimensions
		ctx.translate(-image.width / 2, -image.height / 2);
	}

	ctx.drawImage(image, 0, 0);

	// Get the complete bitmap data - use canvas dimensions, not img dimensions
	const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

	const bitmapData: BitmapData = {
		width: canvas.width,
		height: canvas.height,
		data: imageData.data,
		imageData: imageData,
	};

	return computeAHash(bitmapData, size, size);
}

/**
 * Calculate aHash from base64 string
 *
 * @param {string} base64Image - Base64 encoded image (with or without data URI prefix)
 * @param {number} size - Hash size (default: 16)
 * @param {number} rotation - Rotation angle in degrees (0, 90, 180, 270)
 * @returns {Promise<string>} Hexadecimal hash string
 *
 * @example
 * ```typescript
 * const base64 = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAA...';
 * const hash = await computeAHashFromBase64(base64);
 * console.log(hash);
 * ```
 */
export function computeAHashFromBase64(base64Image: string, size: number = 16, rotation: number = 0): Promise<string> {
	return new Promise((resolve: (value: string) => any, reject) => {
		const image = new Image();
		image.crossOrigin = 'Anonymous';

		// Ensure base64 has proper data URI prefix
		const imageSrc = base64Image.startsWith('data:')
			? base64Image
			: `data:image/png;base64,${base64Image.replace(/^data:image\/\w+;base64,/, '')}`;

		image.onload = async () => {
			try {
				const hash = await computeAHashFromImageElement(image, size, rotation);
				resolve(hash);
			} catch (error) {
				reject(error);
			}
		};

		image.onerror = () => {
			reject(new Error('Failed to load image from base64'));
		};

		image.src = imageSrc;
	});
}
